/**
 * Grymwyrd world configuration — the `WorldConfig` instance for grymwyrd.com.
 *
 * This is the ONE place grymwyrd-specific facets, settings, and unlock books are
 * declared. The shared shell (`lib/api/rpgApi`, `lib/entitlements/contentAccess`)
 * reads these via the active-config singleton; nothing else hardcodes
 * SRD/Grymwyrd literals. Registered at bootstrap in `main.tsx` (and test setup).
 *
 * In P2 this file stays app-level while the shell moves to
 * `packages/@gamecodex/*`; `apps/zweihander` ships its own equivalent.
 */
import type { WorldConfig } from '@gamecodex/world-config'
// The shell's baseline English copy (extracted from Grymwyrd's components in P-B).
// Grymwyrd's copy IS this baseline; other worlds override per-key.
import { baseEn } from '@/content/en'
import { baseDe } from '@/content/de'
import { baseFr } from '@/content/fr'
import { baseEs } from '@/content/es'
import { basePtBR } from '@/content/pt-BR'
import { baseIt } from '@/content/it'
import { basePl } from '@/content/pl'
import { baseRu } from '@/content/ru'
import { baseJa } from '@/content/ja'
import { baseZhCN } from '@/content/zh-CN'
import { baseKo } from '@/content/ko'

// ── Grymwyrd unlock books (RPGPublisher ids) ───────────────────────────────
/** Grymwyrd Cataclysm — owning this (Atlas entitlement) unlocks its content. */
export const GRYMWYRD_UNLOCK_BOOK_ID = 'bc00c79f-3956-4443-8457-f10311050e84'
/** Grymwyrd Galvanist (Vol II) — the second first-party unlock book. */
export const GRYMWYRD_GALVANIST_BOOK_ID = 'ea52b09b-a2ac-4bcc-8f4c-e69dda9146f5'

export const grymwyrdWorldConfig: WorldConfig = {
  worldId: 'grymwyrd',
  worldName: 'Grymwyrd',
  domain: 'grymwyrd.com',

  // English + German. German copy is agent-generated (P-D) and pending a
  // native-speaker review pass before launch.
  locales: {
    default: 'en',
    supported: [
      { code: 'en', label: 'English', native: 'English', dir: 'ltr' },
      { code: 'de', label: 'German', native: 'Deutsch', dir: 'ltr' },
      { code: 'fr', label: 'French', native: 'Français', dir: 'ltr', restricted: true },
      { code: 'es', label: 'Spanish', native: 'Español', dir: 'ltr', restricted: true },
      { code: 'pt-BR', label: 'Portuguese (Brazil)', native: 'Português (Brasil)', dir: 'ltr', restricted: true },
      { code: 'it', label: 'Italian', native: 'Italiano', dir: 'ltr' },
      { code: 'pl', label: 'Polish', native: 'Polski', dir: 'ltr' },
      { code: 'ru', label: 'Russian', native: 'Русский', dir: 'ltr' },
      { code: 'ja', label: 'Japanese', native: '日本語', dir: 'ltr' },
      { code: 'zh-CN', label: 'Chinese (Simplified)', native: '简体中文', dir: 'ltr' },
      { code: 'ko', label: 'Korean', native: '한국어', dir: 'ltr' },
    ],
  },
  // Copy catalogs, composed from the per-feature fragments. `baseEn` IS Grymwyrd's
  // English (P-B); `baseDe` is its German mirror (P-D).
  content: {
    en: { 'common.worldName': 'Grymwyrd', ...baseEn },
    de: { 'common.worldName': 'Grymwyrd', ...baseDe },
    fr: { 'common.worldName': 'Grymwyrd', ...baseFr },
    es: { 'common.worldName': 'Grymwyrd', ...baseEs },
    'pt-BR': { 'common.worldName': 'Grymwyrd', ...basePtBR },
    it: { 'common.worldName': 'Grymwyrd', ...baseIt },
    pl: { 'common.worldName': 'Grymwyrd', ...basePl },
    ru: { 'common.worldName': 'Grymwyrd', ...baseRu },
    ja: { 'common.worldName': 'Grymwyrd', ...baseJa },
    'zh-CN': { 'common.worldName': 'Grymwyrd', ...baseZhCN },
    ko: { 'common.worldName': 'Grymwyrd', ...baseKo },
  },
  branding: {
    images: {
      logo: 'https://media.grymwyrd.com/layout/logoGrymWyrd1.png',
      ogImage: '/og/default.png',
      favicon: '/favicon.png',
      avatarFallback: '/avatars/default.svg',
      parchmentTexture: '/textures/parchment-dark.png',
      splatterTexture: '/textures/blood-splatter.png',
    },
    fonts: {
      display: 'UnifrakturCook',
      heading: 'Cinzel',
      body: 'EB Garamond',
      stylesheetUrl:
        'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400&family=Inter:wght@400;500;600&family=UnifrakturCook:wght@700&display=swap',
    },
    background: { variant: 'mist-fog' },
  },

  // `srd-5-2-1` maps to the live RPG Publisher game system (api canonical code); the rest are forthcoming
  // (entities must be tagged + `/entities` must accept a `game_system` filter).
  gameSystems: [
    { id: 'srd-5-2-1', label: 'SRD 5.2.1', available: true },
    { id: 'zweihander-reforged', label: 'Zweihänder' },
    { id: '2d20', label: '2D20' },
    { id: 'shadowdark', label: 'Shadowdark' },
    { id: 'dragonslayer', label: 'Dragon Slayer' },
  ],
  // Grymwyrd is live (api maps book content to settings, shipped 2026-06-18).
  // Greywick is not tagged yet → selecting it yields an honest empty result.
  // api `pub_setting` "Grymwyrd" — scopes book listings to this world's own shelf.
  settingId: '8fb93531-4e19-452b-b605-9630523e4eff',
  settings: [
    { id: 'grymwyrd', label: 'Grymwyrd', available: true },
    { id: 'greywick', label: 'Greywick' },
  ],

  homeCampaign: {
    slug: 'grymwyrd-cataclysm',
    eyebrow: 'Now Crowdfunding',
    title: 'Grymwyrd: Cataclysm',
    body: 'The world-ending expansion. Back the campaign to unlock the Cataclysm bestiary, new playable lineages, and your name etched into the canon.',
    ctaLabel: 'Back the Cataclysm',
  },

  seo: {
    defaultDescription: 'The living horror-fantasy RPG world where your legend becomes canon.',
    tagline: 'Enter the Eternal Twilight',
    defaultOgImage: '/og/default.png',
  },

  // Open-licensed systems are free for everyone; SRD 5.1/5.2.1 are live, the rest
  // are pre-classified as open so they unlock automatically when content lands.
  openCanonSystemIds: [
    'srd-5-2-1',
    'srd51',
    'srd-5.2.1',
    'srd-5.1',
    'srd5.2.1',
    'srd5.1',
    'dragonslayer',
    'zweihander',
    'evolutionary',
  ],
  // First-party (paid) Grymwyrd canon settings.
  lockedSettingIds: ['grymwyrd', 'greywick'],
  // Per-book gating: an entity locks on whichever of these its provenance names.
  unlockBookIds: [GRYMWYRD_UNLOCK_BOOK_ID, GRYMWYRD_GALVANIST_BOOK_ID],
  // Facet-level / unknown-book gate falls back to Cataclysm.
  fallbackUnlockBookId: GRYMWYRD_UNLOCK_BOOK_ID,
}
